import { Box, Container, Skeleton, Typography } from '@mui/material';
import React, { ReactNode } from 'react';
import { useProfile } from '../api';
import AppBar from '../app-bar/AppBar';
import ksimHeaderSmall from '../assets/ksim-header-small.png';
import ksimHeader from '../assets/ksim-header.png';
import ScheduledExercisesSkeleton from '../scheduled-exercises/ScheduledExercisesSkeleton';

export interface LandingPageProps {
  actions: ReactNode;
  children?: ReactNode;
  scheduledExercises: ReactNode;
  secondaryActions?: ReactNode;
}

const LandingPage = ({ actions, children, scheduledExercises, secondaryActions }: LandingPageProps) => {
  const { profile, loadingProfile, profileError } = useProfile();

  if (profileError) {
    throw profileError;
  }

  const firstName = profile?.name.split(' ')[0];

  return (
    <>
      <AppBar />
      <Container sx={{ my: 4 }}>
        {loadingProfile ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Skeleton sx={{ fontSize: '2.5rem', mb: 4 }} width={302} />
              <Skeleton sx={{ fontSize: '2.5rem', mb: 4 }} width={125} />
            </Box>
            <Skeleton sx={{ mb: 8 }} height={188} width="100%" variant="rectangular" />
            <ScheduledExercisesSkeleton />
          </>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '[left] 1fr [right] auto',
                gridTemplateRows: '[top] auto [middle] auto [bottom] auto',
                rowGap: 3,
                mb: { xs: 4, sm: 0 },
              }}
            >
              <Box sx={{ alignSelf: 'center' }}>
                <Typography component="h1" variant="h4">
                  Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() > 18 ? 'evening' : 'afternoon'}
                  {firstName && `, ${firstName}`}
                </Typography>
              </Box>

              <Box component="picture" sx={{ gridColumnEnd: 'span 2', gridRow: 'middle' }}>
                <source media="(max-width: 599px)" srcSet={ksimHeaderSmall + ' 599w'} sizes="599" />
                <source srcSet={ksimHeader + ' 600w'} sizes="600px" />
                <Box
                  component="img"
                  src={ksimHeader}
                  alt="K-Sim"
                  sx={{ borderRadius: 2, objectFit: 'cover', height: { xs: 131, sm: 193 }, width: '100%' }}
                />
              </Box>
              <Box
                sx={{
                  alignSelf: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  justifySelf: { xs: 'center', sm: 'flex-end' },
                  display: 'flex',
                  gap: 1,
                  gridColumn: { xs: 'left', sm: 'right' },
                  gridRow: { xs: 'bottom', sm: 'top' },
                  whiteSpace: 'wrap',
                }}
              >
                {actions}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography component="h2" variant="h5">
                Exercises
              </Typography>
              {secondaryActions}
            </Box>

            {scheduledExercises}
          </>
        )}
      </Container>

      {children}
    </>
  );
};

export default LandingPage;
